/**
 * 遥测层：Sentry（错误监控）+ Umami（访问统计）
 *
 * 全部按"环境变量缺省 → 自动跳过"的开关式设计：
 *  - 不配置任何变量 → 不加载任何遥测代码，对最终包体几乎无影响（tree-shake 后只剩薄 stub）
 *  - 配上 VITE_SENTRY_DSN → 启用 Sentry
 *  - 配上 VITE_UMAMI_SRC + VITE_UMAMI_WEBSITE_ID → 启用 Umami
 *
 * 这样开发期 / Preview 期 / 内部测试期不会污染统计与告警。
 *
 * 环境变量在 .env.local（不入 git）里配；样本见 .env.example
 */

const env = import.meta.env as ImportMetaEnv

export async function initSentry() {
  const dsn = env.VITE_SENTRY_DSN
  if (!dsn) return

  try {
    const Sentry = await import('@sentry/react')
    Sentry.init({
      dsn,
      environment: env.MODE,
      release: env.VITE_RELEASE_TAG || 'forecast-h5@local',
      // 错误采样 100%（默认）；性能采样 10%（成本可控）
      tracesSampleRate: 0.1,
      // 不指定 integrations 即使用默认（含 GlobalHandlers / InboundFilters 等），
      // 这样未捕获的 error / unhandled promise rejection 会自动上报
      sendDefaultPii: false
    })
    // 业务代码手动 capture 用：window.Sentry（不能用 __SENTRY__，那是 SDK 自己的注册键）
    ;(window as unknown as { Sentry?: unknown }).Sentry = Sentry
  } catch (e) {
    console.warn('[telemetry] Sentry init failed:', e)
  }
}

export function initUmami() {
  const src = env.VITE_UMAMI_SRC
  const id = env.VITE_UMAMI_WEBSITE_ID
  if (!src || !id) return
  if (document.querySelector('script[data-umami]')) return

  const s = document.createElement('script')
  s.async = true
  s.defer = true
  s.src = src
  s.setAttribute('data-website-id', id)
  s.setAttribute('data-umami', '1')
  document.head.appendChild(s)
}

/** 启动入口：在 main.tsx 调一次即可 */
export function initTelemetry() {
  // Sentry 异步加载，不阻塞首屏
  void initSentry()
  initUmami()
}
